/**
 * The seam between the auth provider and the API adapter.
 *
 * The Clerk session lives in React context, but `HttpRepository` is a plain
 * module that is also used outside React (and by the seed/test helpers), so it
 * cannot call a hook. Instead the Clerk layer registers a getter here once, at
 * provider mount, and the adapter asks for a fresh token per request.
 *
 * A *getter* rather than a cached string on purpose: Clerk session tokens are
 * short-lived, so reading the token at request time is what keeps a long-lived
 * admin tab working instead of failing with an expired JWT.
 */

type TokenGetter = () => Promise<string | null>;

let getter: TokenGetter | null = null;

/** Called once by the Clerk layer when a session becomes available. */
export function setApiTokenProvider(fn: TokenGetter | null): void {
  getter = fn;
}

/** Current session token, or null when nobody is signed in. */
export async function getApiToken(): Promise<string | null> {
  if (!getter) return null;
  try {
    return await getter();
  } catch {
    // An auth provider that cannot mint a token means "not signed in" as far as
    // the API is concerned; the server will answer 401 and the UI will react.
    return null;
  }
}
